import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Plus, Check, ArrowLeft, Users2, ChevronDown, ChevronUp, Car, Info, X as XIcon, Repeat, CalendarDays } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { todayISO, addMinutes, computeClassBreakdown, studentShortLabel, formatDateES, appointmentRouteLabel, unavailColor } from "@/lib/utils-app";
import { APPOINTMENT_TYPES, typeConsumesBono } from "@/lib/appointment-types";
import AppointmentContextSidebar from "@/components/autoescuela/AppointmentContextSidebar";
import QuickAddDialog, { QUICK_ADD_VALUE } from "@/components/autoescuela/QuickAddDialog";
import StudentPicker from "@/components/autoescuela/StudentPicker";
import { preferJoseAlfredo } from "@/utils/teacherOrder";

const DEFAULT_FORM = {
  student_id: "",
  date: todayISO(),
  time: "10:00",
  end_time: "10:45",
  pickup_location: "",
  pickup_location_end: "",
  circuit: "",
  appointment_type: "clase_practica",
  vehicle_id: "",
  teacher: "", // V1.39 fix · Se auto-selecciona el 1er profesor cuando /teachers carga (evita código "JA" huérfano).
  franchise: "Natur Autoescuela",
  section: "1",
  notes: "",
  status: "pending",
};

function pad2(n) {
  const v = Math.max(0, parseInt(n || 0, 10));
  return String(v).padStart(2, "0");
}

/**
 * Practice-info label shown next to the student's name in the picker.
 * Format:  P:12 R:04 | F:14/30   (all missing values default to 0)
 */
function studentPracticeLabel(metrics) {
  if (!metrics) return "P:00 R:00 | F:00/00";
  return `P:${pad2(metrics.P)} R:${pad2(metrics.R)} | F:${pad2(metrics.F)}/${pad2(metrics.T)}`;
}

function StudentSelect({ value, onChange, students, metricsById }) {
  // Además del orden por nombre/apellidos, aquí conserva utilidad ordenar
  // por clases restantes (los alumnos con más bono pendiente arriba).
  const extraSorts = useMemo(
    () => [
      {
        key: "restantes",
        label: "Clases restantes",
        compare: (a, b) => {
          const rA = metricsById?.[a.id]?.R ?? 0;
          const rB = metricsById?.[b.id]?.R ?? 0;
          if (rB !== rA) return rB - rA;
          return (a.name || "").localeCompare(b.name || "", "es");
        },
      },
    ],
    [metricsById]
  );
  return (
    <StudentPicker
      label="Alumno *"
      value={value}
      onChange={onChange}
      students={students}
      testId="appt-student-select"
      optionTestIdPrefix="appt-student"
      renderMeta={(s) => `| ${studentPracticeLabel(metricsById?.[s.id])}`}
      extraSorts={extraSorts}
    />
  );
}

function TypeSelect({ value, onChange }) {
  return (
    <div>
      <label className="text-sm font-semibold text-neutral-700 mb-1.5 block">Tipo de cita *</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid="appt-type-select" className="h-11 rounded-xl">
          <SelectValue placeholder="Selecciona el tipo" />
        </SelectTrigger>
        <SelectContent>
          {APPOINTMENT_TYPES.map((t) => (
            <SelectItem
              key={t.value}
              value={t.value}
              data-testid={`appt-type-option-${t.value}`}
            >
              {t.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function LocationSelect({ label, value, onChange, locations, testId, optional, onQuickAdd }) {
  return (
    <div>
      <label className="text-sm font-semibold text-neutral-700 mb-1.5 block">
        {label} {!optional && "*"}
      </label>
      {/* key por nº de ubicaciones: al dar de alta una nueva desde el propio
          desplegable, remonta el Select para que muestre el valor recién
          seleccionado (Radix no refresca el texto si está cerrado). */}
      <Select
        key={`${testId}-${locations.length}`}
        value={value}
        onValueChange={(v) => (v === QUICK_ADD_VALUE ? onQuickAdd?.() : onChange(v))}
      >
        <SelectTrigger data-testid={testId} className="h-11 rounded-xl">
          <SelectValue placeholder="Selecciona una ubicación" />
        </SelectTrigger>
        <SelectContent>
          {locations.map((l) => (
            <SelectItem key={l.id} value={l.name}>
              {l.name}
            </SelectItem>
          ))}
          <SelectItem value={QUICK_ADD_VALUE} data-testid={`${testId}-quick-add`}>
            + Añadir ubicación nueva…
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function toMin(v) {
  const [h, m] = String(v || "").split(":").map(Number);
  return Number.isFinite(h) ? h * 60 + (m || 0) : -1;
}

// V1.61 · Citas del día: visión rápida de la agenda para evitar solapes.
function DayAgendaPanel({ date, time, endTime, excludeId, students = [] }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const nameById = useMemo(() => Object.fromEntries((students || []).map((s) => [s.id, studentShortLabel(s) || s.name])), [students]);

  useEffect(() => {
    if (!date) { setItems([]); return; }
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get("/appointments", { params: { date } }).then(({ data }) => Array.isArray(data) ? data : []).catch(() => []),
      api.get("/teacher-unavailability", { params: { date_from: date, date_to: date } })
        .then(({ data }) => (Array.isArray(data?.items) ? data.items : [])).catch(() => []),
    ]).then(([appts, blocks]) => {
      if (!alive) return;
      const typeLabel = (t) => (t === "clase_practica" ? "Clase práctica" : (t || "Cita").replace(/_/g, " "));
      const rows = [
        ...appts
          .filter((a) => a.status !== "cancelled" && a.id !== excludeId)
          .map((a) => ({
            id: a.id, start: a.time, end: a.end_time || a.time,
            label: `${typeLabel(a.appointment_type)}: ${a.student_name || nameById[a.student_id] || "—"}`,
            route: appointmentRouteLabel(a),
            kind: "cita",
          })),
        ...blocks.map((b) => ({ id: b.id, start: b.all_day ? "00:00" : b.time, end: b.all_day ? "23:59" : b.end_time, label: b.title || "No disponible", route: "", kind: "bloqueo", color: b.color || "gris" })),
      ].sort((a, b) => (a.start || "").localeCompare(b.start || ""));
      setItems(rows);
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [date, excludeId, nameById]);

  const s = toMin(time);
  const e = toMin(endTime);
  const overlaps = (r) => {
    if (s < 0 || e < 0 || e <= s) return false;
    const rs = toMin(r.start);
    let re = toMin(r.end);
    if (rs < 0) return false;
    if (re <= rs) re = rs + 45;
    return s < re && rs < e;
  };

  return (
    <div className="rounded-xl border border-neutral-200 bg-neutral-50/70 overflow-hidden" data-testid="day-agenda-panel">
      <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-neutral-600 border-b border-neutral-200 bg-neutral-100/70 flex items-center gap-1.5">
        <CalendarDays className="h-3 w-3" /> Citas del día {date} ({items.length})
      </div>
      {loading ? (
        <div className="px-3 py-2 text-[11px] text-neutral-500">Cargando agenda…</div>
      ) : items.length === 0 ? (
        <div className="px-3 py-2 text-[11px] text-emerald-700 font-semibold" data-testid="day-agenda-empty">
          Día libre — sin citas ni bloqueos.
        </div>
      ) : (
        <div className="max-h-36 overflow-y-auto divide-y divide-neutral-100">
          {items.map((r) => {
            const clash = overlaps(r);
            return (
              <div key={`${r.kind}-${r.id}`}
                className={"px-3 py-1 text-[11px] " + (clash ? "bg-red-50" : "")}
                data-testid={`day-agenda-row-${r.id}`}>
                <div className="flex items-start gap-2">
                  <span className="font-mono tabular-nums text-neutral-700 shrink-0">{r.start}–{r.end}</span>
                  {r.kind === "bloqueo" && (
                    <span className={"text-[9px] font-bold uppercase rounded px-1 py-0.5 shrink-0 " + unavailColor(r.color).chip} data-color={r.color || "gris"}>Bloqueo</span>
                  )}
                  <span className={"flex-1 min-w-0 break-words " + (clash ? "text-red-800 font-bold" : "text-neutral-700 font-semibold")}>{r.label}</span>
                  {clash && (
                    <span className="text-[9px] font-bold uppercase text-red-700 shrink-0" data-testid={`day-agenda-clash-${r.id}`}>⚠ solapa</span>
                  )}
                </div>
                {r.route && (
                  <div className="pl-[4.5rem] text-[10px] text-neutral-500 break-words" data-testid={`day-agenda-route-${r.id}`}>
                    {r.route}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SessionSummary({ time, endTime, consumesBono = true }) {
  const { numClasses, classes } = useMemo(
    () => computeClassBreakdown(time, endTime),
    [time, endTime]
  );
  if (numClasses === 0) {
    return (
      <div className="rounded-xl bg-neutral-50 border border-neutral-200 px-3 py-2 text-xs text-neutral-500">
        Define una hora de fin posterior a la de inicio para calcular la duración.
      </div>
    );
  }
  // V1.32.1 · Solo las clases prácticas consumen bono. Para exámenes y
  // clases teóricas mostramos únicamente la duración, sin sugerir consumo.
  if (!consumesBono) {
    const totalMin = numClasses * 45;
    return (
      <div
        className="rounded-xl bg-neutral-50 border border-neutral-200 px-3 py-2 text-xs text-neutral-700"
        data-testid="session-summary"
      >
        <div className="font-semibold text-neutral-700 mb-1">
          Duración: {totalMin} min · <span className="text-neutral-500 font-normal">no descuenta bono</span>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-neutral-500">
          <span>Inicio <span className="font-mono">{time}</span></span>
          <span>Fin <span className="font-mono">{endTime}</span></span>
        </div>
      </div>
    );
  }
  return (
    <div
      className="rounded-xl bg-orange-50 border border-orange-200 px-3 py-2 text-xs text-neutral-700"
      data-testid="session-summary"
    >
      <div className="font-semibold text-orange-700 mb-1">
        {numClasses} {numClasses === 1 ? "clase" : "clases"} de 45 min
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
        {classes.map((c) => (
          <span key={c.index}>
            <span className="text-neutral-500">{c.index})</span>{" "}
            <span className="font-mono">{c.time}</span>
          </span>
        ))}
        <span className="text-neutral-500">
          Fin: <span className="font-mono">{endTime}</span>
        </span>
      </div>
    </div>
  );
}

function AddBonoMiniForm({ studentId, onSaved, onCancel }) {
  // V1.42 · Toda clase añadida debe tener origen identificable:
  // Factura (crea registro en pestaña Facturas) o Ajuste manual (PIN + auditoría).
  const [origin, setOrigin] = useState(""); // "" | "invoice" | "adjustment"
  const [saving, setSaving] = useState(false);
  // Factura
  const [inv, setInv] = useState({ number: "", date: todayISO(), classes_included: 10, notes: "", emitted: false });
  // Ajuste manual
  const [adj, setAdj] = useState({ delta: 1, reason: "", notes: "" });

  const inputCls = "w-full rounded-lg border border-neutral-300 bg-white px-2 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500";

  const submitInvoice = async (e) => {
    e.preventDefault();
    if (Number(inv.classes_included) <= 0) return toast.error("Indica el nº de clases contratadas");
    if (inv.emitted && !inv.number.trim()) return toast.error("Para marcar la factura como emitida es necesario un número de factura");
    setSaving(true);
    try {
      await api.post(`/students/${studentId}/invoices`, {
        number: inv.number,
        date: inv.date,
        classes_included: Number(inv.classes_included),
        notes: inv.notes,
        emitted: inv.emitted,
      });
      toast.success("Factura creada · clases añadidas al saldo");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const submitAdjustment = async (e) => {
    e.preventDefault();
    if (!adj.reason.trim()) return toast.error("El motivo del ajuste es obligatorio");
    if (Number(adj.delta) === 0) return toast.error("El ajuste debe ser distinto de 0");
    setSaving(true);
    try {
      await api.post(`/students/${studentId}/class-adjustments`, {
        delta: Number(adj.delta),
        reason: adj.reason,
        notes: adj.notes,
      });
      toast.success("Ajuste aplicado · registrado en el historial");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!origin) {
    return (
      <div className="mt-3 space-y-2" data-testid="add-bono-mini-form">
        <div className="text-[11px] font-semibold text-neutral-600">¿Origen de las clases?</div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setOrigin("invoice")}
            data-testid="bono-origin-invoice"
            className="rounded-lg border border-neutral-300 bg-white hover:border-orange-400 hover:bg-orange-50 px-3 py-2 text-left"
          >
            <div className="text-xs font-bold text-neutral-800">1 · Factura / Venta de bono</div>
            <div className="text-[10px] text-neutral-500 mt-0.5">Crea la factura en la ficha del alumno</div>
          </button>
          <button
            type="button"
            onClick={() => setOrigin("adjustment")}
            data-testid="bono-origin-adjustment"
            className="rounded-lg border border-neutral-300 bg-white hover:border-orange-400 hover:bg-orange-50 px-3 py-2 text-left"
          >
            <div className="text-xs font-bold text-neutral-800">2 · Ajuste manual</div>
            <div className="text-[10px] text-neutral-500 mt-0.5">Requiere PIN maestro · auditado</div>
          </button>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="h-8 px-3 rounded-lg text-xs font-semibold text-neutral-600 hover:bg-neutral-100"
        >
          Cancelar
        </button>
      </div>
    );
  }

  if (origin === "invoice") {
    return (
      <form onSubmit={submitInvoice} className="mt-3 space-y-2" data-testid="bono-invoice-form">
        <div className="text-[11px] font-semibold text-neutral-600">Factura / Venta de bono</div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] text-neutral-500 mb-0.5 block">Nº factura</label>
            <input data-testid="bono-inv-number" type="text" placeholder="Ej. F-2026-001" value={inv.number}
              onChange={(e) => setInv({ ...inv, number: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="text-[10px] text-neutral-500 mb-0.5 block">Fecha emisión</label>
            <input data-testid="bono-inv-date" type="date" value={inv.date}
              onChange={(e) => setInv({ ...inv, date: e.target.value })} required className={inputCls} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 items-end">
          <div>
            <label className="text-[10px] text-neutral-500 mb-0.5 block">Nº clases contratadas</label>
            <input data-testid="bono-inv-classes" type="number" min="1" value={inv.classes_included}
              onChange={(e) => setInv({ ...inv, classes_included: e.target.value })} required className={inputCls} />
          </div>
          <label className="flex items-center gap-2 text-xs h-9">
            <input data-testid="bono-inv-emitted" type="checkbox" checked={inv.emitted}
              onChange={(e) => setInv({ ...inv, emitted: e.target.checked })} className="h-4 w-4 accent-orange-500" />
            Factura emitida
          </label>
        </div>
        <textarea data-testid="bono-inv-notes" rows={2} placeholder="Observaciones" value={inv.notes}
          onChange={(e) => setInv({ ...inv, notes: e.target.value })}
          className="w-full rounded-lg border border-neutral-300 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => setOrigin("")}
            className="h-9 px-3 rounded-lg text-xs font-semibold text-neutral-600 hover:bg-neutral-100">
            Atrás
          </button>
          <button type="submit" disabled={saving} data-testid="bono-mini-submit"
            className="h-9 px-3 rounded-lg text-xs font-semibold bg-orange-500 hover:bg-orange-600 text-white inline-flex items-center gap-1 disabled:opacity-50">
            <Plus className="h-3 w-3" />
            Crear factura y añadir clases
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={submitAdjustment} className="mt-3 space-y-2" data-testid="bono-adjustment-form">
      <div className="text-[11px] font-semibold text-neutral-600">
        Ajuste manual · requiere <span className="text-orange-700">PIN maestro</span> y queda auditado
      </div>
      <div className="grid grid-cols-[110px,1fr] gap-2">
        <div>
          <label className="text-[10px] text-neutral-500 mb-0.5 block">Clases (+/−)</label>
          <input data-testid="bono-adj-delta" type="number" value={adj.delta}
            onChange={(e) => setAdj({ ...adj, delta: e.target.value })} required className={inputCls} />
        </div>
        <div>
          <label className="text-[10px] text-neutral-500 mb-0.5 block">Motivo (obligatorio)</label>
          <input data-testid="bono-adj-reason" type="text" placeholder="Ej. clase regalo, corrección de bono…" value={adj.reason}
            onChange={(e) => setAdj({ ...adj, reason: e.target.value })} required className={inputCls} />
        </div>
      </div>
      <textarea data-testid="bono-adj-notes" rows={2} placeholder="Observaciones" value={adj.notes}
        onChange={(e) => setAdj({ ...adj, notes: e.target.value })}
        className="w-full rounded-lg border border-neutral-300 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => setOrigin("")}
          className="h-9 px-3 rounded-lg text-xs font-semibold text-neutral-600 hover:bg-neutral-100">
          Atrás
        </button>
        <button type="submit" disabled={saving} data-testid="bono-adj-submit"
          className="h-9 px-3 rounded-lg text-xs font-semibold bg-orange-500 hover:bg-orange-600 text-white inline-flex items-center gap-1 disabled:opacity-50">
          <Plus className="h-3 w-3" />
          Aplicar ajuste
        </button>
      </div>
    </form>
  );
}

function BonoIndicator({ summary, summaryLoading, appliesBono, onReload }) {
  const [showForm, setShowForm] = useState(false);
  if (!appliesBono) return null;
  if (summaryLoading) {
    return (
      <div className="rounded-xl bg-neutral-50 border border-neutral-200 px-3 py-2 text-xs text-neutral-500">
        Cargando bono…
      </div>
    );
  }
  if (!summary) return null;
  const remaining = summary.remaining;
  const exhausted = remaining <= 0;
  return (
    <div
      data-testid="bono-indicator"
      className={
        "rounded-xl border px-3 py-2.5 text-sm " +
        (exhausted
          ? "bg-red-50 border-red-300 text-red-900"
          : "bg-emerald-50 border-emerald-200 text-emerald-900")
      }
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold opacity-70">
            Bono del alumno
          </div>
          <div className="font-bold text-base">
            Clases restantes:{" "}
            <span data-testid="bono-remaining" className="font-mono">
              {remaining}
            </span>
          </div>
          <div className="text-[11px] opacity-70">
            {summary.contracted} contratadas · {summary.done} realizadas · {summary.scheduled} programadas
          </div>
        </div>
        {exhausted && (
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            data-testid="bono-add-toggle"
            className="text-xs font-semibold inline-flex items-center gap-1 bg-white text-red-700 border border-red-300 hover:bg-red-100 px-3 h-9 rounded-lg"
          >
            <Plus className="h-3.5 w-3.5" />
            Añadir bono
          </button>
        )}
      </div>
      {exhausted && (
        <div className="mt-2 text-xs flex items-start gap-1.5">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>El alumno no tiene clases disponibles.</span>
        </div>
      )}
      {showForm && (
        <AddBonoMiniForm
          studentId={summary.student.id}
          onSaved={() => {
            setShowForm(false);
            onReload?.();
          }}
          onCancel={() => setShowForm(false)}
        />
      )}
    </div>
  );
}

function BonoOverConfirmation({ remaining, onContinue, onBack }) {
  return (
    <div
      data-testid="bono-confirmation"
      className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-3"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
        <div>
          <div className="font-bold text-amber-900 mb-1">Bono agotado</div>
          <p className="text-sm text-amber-800">
            El alumno ha alcanzado el número de clases contratadas (restan{" "}
            <span className="font-mono font-bold">{remaining}</span>). Para
            continuar programando más clases, será necesario que adquiera un
            nuevo bono.
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onBack}
          data-testid="bono-confirm-back"
          className="btn-secondary flex-1"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver
        </button>
        <button
          type="button"
          onClick={onContinue}
          data-testid="bono-confirm-continue"
          className="inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-xl px-4 py-2.5 transition-colors h-11 text-sm flex-1"
        >
          <Check className="h-4 w-4" />
          Crear igualmente
        </button>
      </div>
    </div>
  );
}

// V1.51 · Selector de tasa DGT para citas de examen. Muestra la fórmula
// restante de la tasa elegida; la cita congela una oportunidad al crearse.
// V1.79 · Tasa (derechos de examen DGT): 2 convocatorias compartidas entre
// teórico y práctico. Obligatoria para cualquier cita de examen.
function ExamRequestPicker({ studentId, value, onChange }) {
  const [reqs, setReqs] = useState([]);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ tasas_number: "", presented_date: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/students/${studentId}/tasas-unificadas`);
      setReqs(data || []);
      if (!value) {
        const free = (data || []).find((r) => r.id && !r.state?.exhausted);
        if (free) onChange(free.id, free.dgt_tasa_id || "");
      }
    } catch {
      setReqs([]);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  // Una tasa del repositorio DGT sin solicitud se "adopta" al elegirla.
  const select = async (rowKey) => {
    const row = reqs.find((r) => (r.id || `dgt:${r.dgt_tasa_id}`) === rowKey);
    if (!row) { onChange("", ""); return; }
    if (row.id) { onChange(row.id, row.dgt_tasa_id || ""); return; }
    try {
      const { data } = await api.post(`/students/${studentId}/exam-requests/adopt`, {
        tasas_number: row.tasas_number,
        presented_date: row.presented_date || "",
      });
      await load();
      onChange(data.id, row.dgt_tasa_id || "");
      toast.success("Tasa vinculada a la convocatoria");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo vincular la tasa");
    }
  };

  const create = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/students/${studentId}/exam-requests`, {
        tasas_number: draft.tasas_number.trim(),
        presented_date: draft.presented_date || new Date().toISOString().slice(0, 10),
      });
      const { data: fresh } = await api.get(`/students/${studentId}/tasas-unificadas`);
      setReqs(fresh || []);
      onChange(data.id, (fresh || []).find((r) => r.id === data.id)?.dgt_tasa_id || "");
      setAdding(false);
      setDraft({ tasas_number: "", presented_date: "" });
      toast.success("Tasa añadida");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo añadir la tasa");
    } finally { setSaving(false); }
  };

  const selected = reqs.find((r) => r.id === value);
  return (
    <div className="rounded-xl border border-orange-200 bg-orange-50/40 px-3 py-2.5 space-y-1.5" data-testid="appt-exam-request-block">
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs font-bold text-orange-900 uppercase tracking-wider">Tasa · derechos de examen *</label>
        <button
          type="button" onClick={() => setAdding((v) => !v)}
          data-testid="appt-add-tasa-btn"
          className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-md border border-orange-300 bg-white hover:bg-orange-50"
        >
          <Plus className="h-3 w-3" /> Añadir tasas
        </button>
      </div>
      <select
        value={value || ""}
        onChange={(e) => select(e.target.value)}
        data-testid="appt-exam-request-select"
        className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-9 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
      >
        <option value="">— Elige la tasa —</option>
        {reqs.map((r) => (
          <option
            key={r.id || `dgt:${r.dgt_tasa_id}`}
            value={r.id || `dgt:${r.dgt_tasa_id}`}
            disabled={r.state?.exhausted}
          >
            {r.tasas_number || "(sin nº)"} · {r.state?.label || "sin usar"}
          </option>
        ))}
      </select>
      {selected?.state && (
        <p className="text-[11px] text-neutral-600">
          Saldo: <b>{selected.state.label}</b> · consumidas {selected.state.consumed}/{selected.state.total}
        </p>
      )}
      {reqs.length === 0 && !adding && (
        <p className="text-[11px] text-amber-700 font-semibold" data-testid="appt-no-tasa-warning">
          Sin tasas registradas: añade una o se pedirá el PIN maestro al guardar.
        </p>
      )}
      {adding && (
        <div className="rounded-lg border-2 border-dashed border-orange-300 bg-white p-2 space-y-1.5">
          <input
            type="text" inputMode="numeric" placeholder="Nº de tasas (p. ej. 412524488018)"
            value={draft.tasas_number}
            onChange={(e) => setDraft((s) => ({ ...s, tasas_number: e.target.value }))}
            data-testid="appt-new-tasa-number"
            className="w-full py-1.5 px-2 text-xs rounded-md border border-neutral-300 font-mono"
          />
          <input
            type="date" value={draft.presented_date}
            onChange={(e) => setDraft((s) => ({ ...s, presented_date: e.target.value }))}
            data-testid="appt-new-tasa-date"
            className="w-full py-1.5 px-2 text-xs rounded-md border border-neutral-300"
          />
          <button
            type="button" onClick={create} disabled={saving || !draft.tasas_number.trim()}
            data-testid="appt-new-tasa-save"
            className="w-full py-1.5 text-xs font-semibold rounded-md bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60"
          >
            {saving ? "Guardando…" : "Guardar y seleccionar"}
          </button>
        </div>
      )}
    </div>
  );
}

function TasaSelector({ studentId, value, onChange }) {
  const [tasas, setTasas] = useState([]);
  const [quickAdd, setQuickAdd] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/students/${studentId}/dgt-tasas`);
      const list = (data || []).filter((t) => t.tipo === "2.1");
      setTasas(list);
      return list;
    } catch {
      setTasas([]);
      return [];
    }
  }, [studentId]);
  useEffect(() => { load(); }, [load]);
  const selected = tasas.find((t) => t.id === value);
  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50/40 px-3 py-2.5 space-y-1.5">
      <label className="text-xs font-bold text-blue-900 uppercase tracking-wider">Tasa DGT a consumir</label>
      <select
        value={value || ""}
        onChange={(e) => (e.target.value === QUICK_ADD_VALUE ? setQuickAdd(true) : onChange(e.target.value))}
        data-testid="appt-tasa-select"
        className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-9 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="">— Sin tasa vinculada —</option>
        {tasas.map((t) => (
          <option key={t.id} value={t.id} disabled={["agotada", "completada", "caducada"].includes(t.consumption?.estado)}>
            {t.number} · {t.consumption?.estado} · libres {t.consumption?.libres}
            {t.consumption?.congeladas ? ` · congeladas ${t.consumption.congeladas}` : ""}
          </option>
        ))}
        <option value={QUICK_ADD_VALUE}>+ Añadir tasa nueva…</option>
      </select>
      <QuickAddDialog
        open={quickAdd}
        entity="tasa"
        extra={{ student_id: studentId }}
        onClose={() => setQuickAdd(false)}
        onCreated={async (doc) => {
          setQuickAdd(false);
          await load();
          if (doc?.id) onChange(doc.id);
        }}
      />
      {selected && (
        <p className="text-[11px] font-semibold text-blue-900" data-testid="appt-tasa-formula">{selected.consumption?.formula}</p>
      )}
      {tasas.length === 0 && (
        <p className="text-[11px] text-neutral-500 italic">
          El alumno no tiene tasas 2.1. Usa «+ Añadir tasa nueva…» en el desplegable para darla de alta aquí mismo.
        </p>
      )}
    </div>
  );
}

function ActiveExamRequestNotice({ studentId, onCreateRequest }) {
  const [state, setState] = useState({ loading: true, hasActive: false });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, hasActive: false });
    (async () => {
      try {
        const { data } = await api.get(`/students/${studentId}/exam-requests`);
        if (cancelled) return;
        const hasActive = data.some((r) => r.status === "active");
        setState({ loading: false, hasActive });
      } catch (err) {
        if (!cancelled) setState({ loading: false, hasActive: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  if (state.loading) {
    return (
      <div
        data-testid="exam-req-check-loading"
        className="rounded-xl bg-neutral-50 border border-neutral-200 px-3 py-2 text-xs text-neutral-500"
      >
        Comprobando solicitudes de examen…
      </div>
    );
  }
  if (state.hasActive) {
    return (
      <div
        data-testid="exam-req-check-ok"
        className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900 flex items-center gap-2"
      >
        <Check className="h-4 w-4" />
        <span>El alumno tiene una solicitud de examen activa.</span>
      </div>
    );
  }
  return (
    <div
      data-testid="exam-req-check-missing"
      className="rounded-xl border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-900"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="font-bold">El alumno no dispone de una solicitud de examen activa.</div>
          <p className="text-xs mt-0.5 opacity-90">
            Registra una nueva solicitud desde el perfil del alumno antes de asignar el examen.
          </p>
        </div>
      </div>
    </div>
  );
}

// V1.XX · Panel desplegable con la disponibilidad declarada del alumno
// y sugerencias inteligentes de horarios reservables. Al pulsar una
// sugerencia el formulario se autocompleta con fecha e intervalo.
const WEEKDAY_LABELS = [
  { key: "monday",    short: "Lun" },
  { key: "tuesday",   short: "Mar" },
  { key: "wednesday", short: "Mié" },
  { key: "thursday",  short: "Jue" },
  { key: "friday",    short: "Vie" },
  { key: "saturday",  short: "Sáb" },
  { key: "sunday",    short: "Dom" },
];

function formatUpdatedAt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

function AvailabilityInsightPanel({
  open,
  onToggle,
  availability,
  suggestions,
  suggestLoading,
  onApplySuggestion,
  teacher,
  duration,
  studentId,
}) {
  const weekly = (availability?.weekly_slots) || {};
  return (
    <div className="mt-2 pl-6" data-testid="availability-panel">
      <button
        type="button"
        onClick={onToggle}
        data-testid="availability-panel-toggle"
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-900 hover:text-amber-950 underline underline-offset-2"
      >
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        {open ? "Ocultar disponibilidad" : "Ver disponibilidad del alumno"}
      </button>
      {open && (
        <div className="mt-2 rounded-lg border border-amber-200 bg-white p-3 space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-600">
                Disponibilidad declarada
              </div>
              {availability?.updated_at && (
                <div
                  className="text-[10px] text-neutral-500"
                  data-testid="availability-panel-updated"
                  title={availability.updated_at}
                >
                  Actualizado: {formatUpdatedAt(availability.updated_at)}
                  {availability.updated_via ? ` · ${availability.updated_via}` : ""}
                </div>
              )}
            </div>
            <div
              className="grid grid-cols-7 gap-1 text-[10px]"
              data-testid="availability-panel-weekly"
            >
              {WEEKDAY_LABELS.map((d) => {
                const slots = weekly[d.key] || [];
                const has = slots.some((s) => s.start && s.end);
                return (
                  <div
                    key={d.key}
                    className={
                      "rounded-md border px-1.5 py-1 " +
                      (has
                        ? "border-emerald-200 bg-emerald-50/60"
                        : "border-neutral-200 bg-neutral-50")
                    }
                    data-testid={`availability-panel-day-${d.key}`}
                  >
                    <div className="font-semibold text-neutral-700 mb-0.5">{d.short}</div>
                    {has ? (
                      slots.filter((s) => s.start && s.end).map((s, i) => (
                        <div key={i} className="font-mono text-emerald-800 leading-tight">
                          {s.start}–{s.end}
                        </div>
                      ))
                    ) : (
                      <div className="text-neutral-400">—</div>
                    )}
                  </div>
                );
              })}
            </div>
            {availability?.preferences && (
              <div className="mt-2 text-[11px] text-neutral-600">
                <span className="font-semibold">Preferencias:</span>{" "}
                {availability.preferences}
              </div>
            )}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-600 mb-1.5">
              Próximos horarios compatibles
              <span className="ml-1 text-[10px] font-normal text-neutral-500 normal-case tracking-normal">
                (profesor: {teacher || "—"} · {duration} min)
              </span>
            </div>
            {!studentId ? (
              <div className="text-[11px] text-neutral-500">
                Selecciona un alumno para ver sugerencias.
              </div>
            ) : suggestLoading ? (
              <div className="text-[11px] text-neutral-500">Buscando huecos reservables…</div>
            ) : suggestions.length === 0 ? (
              <div className="text-[11px] text-neutral-500" data-testid="availability-panel-no-suggestions">
                No se han encontrado huecos libres en los próximos días
                dentro de la disponibilidad del alumno.
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="availability-panel-suggestions">
                {suggestions.map((s, idx) => (
                  <button
                    key={`${s.date}-${s.time}-${idx}`}
                    type="button"
                    onClick={() => onApplySuggestion(s)}
                    data-testid={`availability-suggest-${idx}`}
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-800 hover:bg-emerald-100 text-[11px] font-semibold px-2.5 py-1 transition-colors"
                    title="Aplicar este horario al formulario"
                  >
                    <span className="font-mono">{s.date}</span>
                    <span className="opacity-60">·</span>
                    <span className="font-mono">{s.time}–{s.end_time}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="mt-2 text-[10px] text-neutral-500">
              Los huecos consideran disponibilidad del alumno, agenda del
              profesor y exámenes ya asignados.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AppointmentDialog({ open, appointment, initialDate, students, onClose, onSaved }) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [circuits, setCircuits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [confirmingOver, setConfirmingOver] = useState(false);
  const [overlaps, setOverlaps] = useState([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [addingShared, setAddingShared] = useState(null); // overlap id being submitted
  const [sharedForm, setSharedForm] = useState(null); // {overlap, pickup_location, pickup_time, pickup_location_end}
  const [practiceMetrics, setPracticeMetrics] = useState({}); // {studentId: {P,R,F,T}}
  // V1.37.15 · Contadores dinámicos de "clases × minutos" para autocalcular Fin
  // a partir de Inicio. No se envían al backend: sólo derivan end_time.
  const [minutesPerClass, setMinutesPerClass] = useState(45);
  // V1.38 · Fuente única de profesores = `/teachers`. Ver StudentsTab, StudentDialog,
  // ConvocatoriaWizard y DgtDocumentsDialog para el mismo criterio.
  const [teacherOptions, setTeacherOptions] = useState([]); // [{code,label}]
  const [classesCount, setClassesCount] = useState(1);
  // V1.49 · Serie semanal: repite la cita en los días marcados durante N semanas.
  const [repeatOpen, setRepeatOpen] = useState(false);
  const [repeatDays, setRepeatDays] = useState([]); // getDay() ints (0=Dom)
  const [repeatWeeks, setRepeatWeeks] = useState(4);
  const [repeatRows, setRepeatRows] = useState([]); // {date,time,end_time,enabled,overlaps}
  // V1.65 · Alta rápida desde los desplegables: {entity, apply(doc)}
  const [quickAdd, setQuickAdd] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [c, l, v, m, t] = await Promise.all([
          api.get("/circuits"),
          api.get("/locations"),
          api.get("/vehicles"),
          api.get("/students/practice-metrics"),
          api.get("/teachers", { params: { include_inactive: false } }).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setCircuits(c.data);
        setLocations(l.data);
        setVehicles(v.data);
        const map = {};
        for (const s of m.data || []) map[s.id] = s;
        setPracticeMetrics(map);
        // V1.38 · Convertimos la lista de /teachers en códigos consistentes:
        // 2 iniciales cuando el nombre tiene ≥2 palabras, 2 primeras letras
        // cuando es una sola. Deduplicamos para evitar colisiones visuales.
        const seen = new Set();
        const opts = (t.data || [])
          .filter((p) => p.active !== false)
          .map((p) => {
            const name = (p.name || "").trim();
            const parts = name.split(/\s+/).filter(Boolean);
            let code = "";
            if (parts.length >= 2) code = (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
            else if (parts.length === 1) code = parts[0].slice(0, 2).toUpperCase();
            return { code, label: name || code };
          })
          .filter((o) => o.code && !seen.has(o.code) && (seen.add(o.code), true));
        const ordered = preferJoseAlfredo(opts);
        setTeacherOptions(ordered);
        // V1.39 fix · Auto-selecciona el primer profesor si el form está vacío
        // (crear sesión nueva) — evita persistir un código huérfano.
        // V1.58 · El primero es siempre José Alfredo (profesor por defecto).
        if (ordered.length > 0) {
          setForm((f) => (f.teacher ? f : { ...f, teacher: ordered[0].code }));
        }
      } catch (err) {
        console.warn("Could not load circuits/locations/vehicles/metrics:", err);
      }
    })();
    const initialTime = appointment?.time || "10:00";
    const initialEndTime = appointment?.end_time || addMinutes(initialTime, 45);
    setForm({
      student_id: appointment?.student_id || "",
      date: appointment?.date || initialDate || todayISO(),
      time: initialTime,
      end_time: initialEndTime,
      pickup_location: appointment?.pickup_location || "",
      pickup_location_end: appointment?.pickup_location_end || "",
      circuit: appointment?.circuit || "",
      appointment_type: appointment?.appointment_type || "clase_practica",
      vehicle_id: appointment?.vehicle_id || "",
      teacher: appointment?.teacher || "JA",
      franchise: appointment?.franchise || "Natur Autoescuela",
      section: appointment?.section || "1",
      notes: appointment?.notes || "",
      status: appointment?.status || "pending",
      tasa_id: appointment?.tasa_id || "",
      exam_request_id: appointment?.exam_request_id || "",
    });
    // Reconstruir contadores a partir del intervalo. Si es una cita existente
    // con duración divisible por 45 usamos 45 min/clase; si no, ajustamos
    // "minutos" al total de un único bloque.
    {
      const { numClasses } = computeClassBreakdown(initialTime, initialEndTime);
      if (numClasses > 0 && (initialEndTime !== "" && initialTime !== "")) {
        // Detectar duración total en minutos
        const [sh, sm] = initialTime.split(":").map(Number);
        const [eh, em] = initialEndTime.split(":").map(Number);
        const total = (eh * 60 + em) - (sh * 60 + sm);
        if (total > 0 && total % 45 === 0) {
          setMinutesPerClass(45);
          setClassesCount(total / 45);
        } else if (total > 0) {
          setMinutesPerClass(total);
          setClassesCount(1);
        } else {
          setMinutesPerClass(45);
          setClassesCount(1);
        }
      } else {
        setMinutesPerClass(45);
        setClassesCount(1);
      }
    }
    setConfirmingOver(false);
    setOverlaps([]);
    setRepeatOpen(false);
    setRepeatDays([]);
    setRepeatWeeks(4);
    setRepeatRows([]);
    // V1.XX · Al editar, abrimos "Detalles avanzados" automáticamente para
    // exponer profesor, vehículo, sección y estado sin un clic extra.
    setAdvancedOpen(!!appointment?.id);
    return () => {
      cancelled = true;
    };
  }, [open, appointment, initialDate]);

  const activeStudents = useMemo(
    // El orden lo decide el propio selector (nombre · apellidos · restantes).
    () => students.filter((s) => s.active),
    [students]
  );

  const studentId = form.student_id;
  const appliesBono = typeConsumesBono(form.appointment_type);

  const loadSummary = useCallback(async () => {
    if (!studentId || !appliesBono) {
      setSummary(null);
      return;
    }
    setSummaryLoading(true);
    try {
      const { data } = await api.get(`/students/${studentId}/summary`);
      setSummary(data);
    } catch (err) {
      console.warn("Could not load student summary:", err);
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [studentId, appliesBono]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // -------- Overlap detection --------
  // Only for practical lessons and only when creating (not editing).
  const shouldCheckOverlap =
    open &&
    !appointment?.id &&
    appliesBono &&
    form.date &&
    form.time &&
    form.end_time &&
    form.end_time > form.time;

  useEffect(() => {
    if (!shouldCheckOverlap) {
      setOverlaps([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/appointments/overlaps", {
          params: { date: form.date, time: form.time, end_time: form.end_time },
        });
        // Hide own student — the dialog already knows about them.
        const filtered = data.filter((o) => {
          if (!studentId) return true;
          if (o.main_student?.id === studentId) return false;
          if ((o.shared_students || []).some((s) => s.id === studentId)) return false;
          return true;
        });
        setOverlaps(filtered);
      } catch (_) {
        setOverlaps([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [shouldCheckOverlap, form.date, form.time, form.end_time, studentId]);

  // Availability warnings (declared weekly slots + active incidents) — purely
  // informational: the admin remains sovereign. "La IA propone, el gestor decide."
  const [availabilityWarnings, setAvailabilityWarnings] = useState([]);
  useEffect(() => {
    if (!studentId || !form.date) { setAvailabilityWarnings([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/students/${studentId}/availability-check`, {
          params: { date: form.date, time: form.time || "" },
        });
        setAvailabilityWarnings(Array.isArray(data?.warnings) ? data.warnings : []);
      } catch (_) {
        setAvailabilityWarnings([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [studentId, form.date, form.time]);

  // V1.35 · Contexto completo del alumno para la barra lateral
  // (Ley 2 · Contexto único). Un único endpoint agrega:
  // cabecera + zona/profesor habitual + bono + próximo examen +
  // disponibilidad + incidencias.
  const [studentContext, setStudentContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);

  useEffect(() => {
    if (!studentId) { setStudentContext(null); return; }
    let cancelled = false;
    setContextLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/students/${studentId}/appointment-context`);
        if (!cancelled) setStudentContext(data);
      } catch (_) {
        if (!cancelled) setStudentContext(null);
      } finally {
        if (!cancelled) setContextLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [studentId]);

  // V1.XX · Panel "Ver disponibilidad" — carga los slots semanales del alumno
  // y sugiere horarios reservables considerando disponibilidad + profesor +
  // citas existentes + exámenes + duración.
  const [studentAvailability, setStudentAvailability] = useState(null);
  const [showAvailability, setShowAvailability] = useState(false);
  const [suggestSlots, setSuggestSlots] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);

  useEffect(() => {
    if (!studentId) { setStudentAvailability(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/students/${studentId}/availability`);
        if (!cancelled) setStudentAvailability(data);
      } catch (_) {
        if (!cancelled) setStudentAvailability(null);
      }
    })();
    return () => { cancelled = true; };
  }, [studentId]);

  // Reset panel cuando cambia el alumno o se cierra el diálogo
  useEffect(() => { setShowAvailability(false); }, [studentId, open]);

  // V1.35 · Sugerencias SIEMPRE visibles (Ley 4 · Menos clics). Se recalculan
  // cuando cambia el alumno, profesor, fecha base o duración.
  useEffect(() => {
    if (!studentId) { setSuggestSlots([]); return; }
    const duration = Math.max(15, classesCount * minutesPerClass);
    setSuggestLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/appointments/suggest-slots", {
          params: {
            student_id: studentId,
            teacher: form.teacher || "",
            from_date: form.date,
            duration_min: duration,
            max_suggestions: 6,
          },
        });
        setSuggestSlots(Array.isArray(data?.suggestions) ? data.suggestions : []);
      } catch (_) {
        setSuggestSlots([]);
      } finally {
        setSuggestLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [studentId, form.teacher, form.date, classesCount, minutesPerClass]);

  const applySuggestion = (s) => {
    const total = classesCount * minutesPerClass;
    const newEnd = addMinutes(s.time, total);
    setForm((f) => ({ ...f, date: s.date, time: s.time, end_time: newEnd }));
  };

  // V1.35 · Toggle de la pestaña "Contexto" en móvil (Ley 4 · Menos clics
  // pero cabe en pantallas pequeñas). En desktop la sidebar es fija.
  const [showContextMobile, setShowContextMobile] = useState(false);
  useEffect(() => { setShowContextMobile(false); }, [studentId, open]);

  const addAsShared = async (overlap, sharedPayload) => {
    if (!studentId) {
      toast.error("Selecciona un alumno primero");
      return;
    }
    if (!sharedPayload) {
      // First click — open the inline form pre-filled with the overlap's own pickup data.
      setSharedForm({
        overlap,
        pickup_location: overlap.pickup_location || form.pickup_location || "",
        pickup_time: overlap.time || form.time || "",
        pickup_location_end: "",
      });
      return;
    }
    setAddingShared(overlap.id);
    try {
      // PIN-protected — axios interceptor prompts the user automatically.
      await api.post(`/appointments/${overlap.id}/shared-students`, {
        student_id: studentId,
        pickup_location: sharedPayload.pickup_location.trim(),
        pickup_time: sharedPayload.pickup_time.trim(),
        pickup_location_end: (sharedPayload.pickup_location_end || "").trim() || null,
      });
      toast.success("Alumno añadido en sesión compartida");
      setSharedForm(null);
      onSaved?.();
      onClose?.();
    } catch (err) {
      const status = err?.response?.status;
      if (status !== 440 && status !== 428) {
        toast.error(formatApiError(err?.response?.data?.detail));
      }
    } finally {
      setAddingShared(null);
    }
  };

  const handleTimeChange = (newTime) => {
    // Al cambiar el inicio, recalculamos Fin usando el bloque activo
    // (clases × minutos por clase). Esto permite que el contador siga siendo
    // la fuente de verdad del intervalo.
    setForm((f) => ({
      ...f,
      time: newTime,
      end_time: addMinutes(newTime, classesCount * minutesPerClass),
    }));
  };

  const handleClassesChange = (raw) => {
    const n = Math.max(1, parseInt(raw || 0, 10) || 1);
    setClassesCount(n);
    setForm((f) => ({
      ...f,
      end_time: addMinutes(f.time, n * minutesPerClass),
    }));
  };

  const handleMinutesPerClassChange = (raw) => {
    const n = Math.max(1, parseInt(raw || 0, 10) || 1);
    setMinutesPerClass(n);
    setForm((f) => ({
      ...f,
      end_time: addMinutes(f.time, classesCount * n),
    }));
  };

  const handleEndTimeChange = (newEnd) => {
    // Edición manual del Fin: sincronizamos el contador de clases si el
    // intervalo resultante es múltiplo exacto de minutos por clase, para
    // mantener coherencia visual.
    setForm((f) => ({ ...f, end_time: newEnd }));
    const [sh, sm] = (form.time || "00:00").split(":").map(Number);
    const [eh, em] = (newEnd || "00:00").split(":").map(Number);
    const total = (eh * 60 + em) - (sh * 60 + sm);
    if (total > 0 && minutesPerClass > 0 && total % minutesPerClass === 0) {
      setClassesCount(total / minutesPerClass);
    }
  };

  // ---- V1.49 · Serie semanal ----
  const seriesDuration = useMemo(() => {
    const [sh, sm] = (form.time || "0:0").split(":").map(Number);
    const [eh, em] = (form.end_time || "0:0").split(":").map(Number);
    const total = (eh * 60 + em) - (sh * 60 + sm);
    return total > 0 ? total : 45;
  }, [form.time, form.end_time]);

  useEffect(() => {
    if (!repeatOpen || appointment?.id || repeatDays.length === 0 || !form.date) {
      setRepeatRows([]);
      return;
    }
    const base = new Date(form.date + "T00:00:00");
    if (isNaN(base.getTime())) return;
    const isos = [];
    for (let d = 0; d < repeatWeeks * 7; d++) {
      const dt = new Date(base);
      dt.setDate(base.getDate() + d);
      if (!repeatDays.includes(dt.getDay())) continue;
      isos.push(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`);
    }
    setRepeatRows((prev) =>
      isos.map((iso) => prev.find((r) => r.date === iso) ||
        { date: iso, time: form.time, end_time: form.end_time, enabled: true, overlaps: null })
    );
  }, [repeatOpen, repeatDays, repeatWeeks, form.date, form.time, form.end_time, appointment?.id]);

  useEffect(() => {
    const pending = repeatRows.filter((r) => r.overlaps === null);
    if (pending.length === 0) return;
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        pending.map((r) =>
          api.get("/appointments/overlaps", { params: { date: r.date, time: r.time, end_time: r.end_time } })
            .then((res) => ({ date: r.date, list: res.data || [] }))
            .catch(() => ({ date: r.date, list: [] }))
        )
      );
      if (cancelled) return;
      setRepeatRows((rows) =>
        rows.map((r) => {
          const f = results.find((x) => x.date === r.date && r.overlaps === null);
          return f ? { ...r, overlaps: f.list } : r;
        })
      );
    })();
    return () => { cancelled = true; };
  }, [repeatRows]);

  const setRowTime = (date, t) => {
    setRepeatRows((rows) =>
      rows.map((r) => (r.date === date ? { ...r, time: t, end_time: addMinutes(t, seriesDuration), overlaps: null } : r))
    );
  };
  const toggleRow = (date) => {
    setRepeatRows((rows) => rows.map((r) => (r.date === date ? { ...r, enabled: !r.enabled } : r)));
  };
  const toggleRepeatDay = (d) => {
    setRepeatDays((days) => (days.includes(d) ? days.filter((x) => x !== d) : [...days, d]));
  };
  const seriesRows = !appointment?.id && repeatOpen ? repeatRows.filter((r) => r.enabled) : [];
  const seriesActive = seriesRows.length > 0;

  const validate = () => {
    if (
      !form.student_id ||
      !form.date ||
      !form.time ||
      !form.end_time ||
      !form.pickup_location ||
      !form.circuit ||
      !form.appointment_type
    ) {
      toast.error("Completa todos los campos obligatorios");
      return false;
    }
    if (form.end_time <= form.time) {
      toast.error("La hora de fin debe ser posterior a la de inicio");
      return false;
    }
    return true;
  };

  const isUnavailBlock = (err) =>
    err?.response?.status === 409 &&
    String(err?.response?.data?.detail || "").includes("bloqueo de indisponibilidad");
  const confirmOverride = (detail) =>
    window.confirm(`${detail}\n\n¿Guardar la cita de todas formas solapando el bloqueo del profesor?`);

  const doSave = async () => {
    const payload = {
      ...form,
      pickup_location_end: form.pickup_location_end || form.pickup_location,
    };
    const { numClasses } = computeClassBreakdown(form.time, form.end_time);
    setSaving(true);
    try {
      if (!appointment?.id && seriesActive) {
        // V1.49 · Creación en serie: reutiliza el POST existente por cada fecha.
        let ok = 0;
        let fail = 0;
        let overrideAll = false; // V1.63 · confirmar una vez y aplicar a toda la serie
        for (const r of seriesRows) {
          const body = { ...payload, date: r.date, time: r.time, end_time: r.end_time };
          try {
            await api.post("/appointments", { ...body, override_unavailability: overrideAll });
            ok++;
          } catch (err) {
            if (isUnavailBlock(err) && (overrideAll || confirmOverride(err.response.data.detail))) {
              overrideAll = true;
              try {
                await api.post("/appointments", { ...body, override_unavailability: true });
                ok++;
                continue;
              } catch (_) { /* cae a fail */ }
            }
            fail++;
          }
        }
        if (fail > 0) toast.warning(`${ok} citas creadas · ${fail} con error`);
        else toast.success(`${ok} citas creadas (serie semanal)`, { duration: 5000 });
        onSaved?.();
        return;
      }
      if (appointment?.id) {
        try {
          await api.patch(`/appointments/${appointment.id}`, payload);
        } catch (err) {
          if (!isUnavailBlock(err) || !confirmOverride(err.response.data.detail)) throw err;
          await api.patch(`/appointments/${appointment.id}`, { ...payload, override_unavailability: true });
        }
        toast.success("Cita actualizada");
      } else {
        try {
          await api.post("/appointments", payload);
        } catch (err) {
          if (!isUnavailBlock(err) || !confirmOverride(err.response.data.detail)) throw err;
          await api.post("/appointments", { ...payload, override_unavailability: true });
        }
        // Re-fetch summary to show updated remaining balance in the toast.
        if (appliesBono && studentId) {
          try {
            const { data } = await api.get(`/students/${studentId}/summary`);
            const clsWord = numClasses === 1 ? "clase descontada" : "clases descontadas";
            toast.success(
              `Cita creada · ${numClasses} ${clsWord}. Restan ${data.remaining} de ${data.contracted}.`,
              { duration: 5000 }
            );
          } catch (_) {
            toast.success("Cita creada");
          }
        } else {
          toast.success("Cita creada");
        }
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    // For new practical lessons, require explicit confirmation when bono is over.
    const isNew = !appointment?.id;
    const { numClasses } = computeClassBreakdown(form.time, form.end_time);
    const overBono =
      isNew &&
      appliesBono &&
      summary &&
      (summary.remaining - numClasses * (seriesActive ? seriesRows.length : 1)) < 0;
    if (overBono && !confirmingOver) {
      setConfirmingOver(true);
      return;
    }
    await doSave();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="sm:max-w-5xl max-h-[90vh] overflow-y-auto"
        data-testid="appointment-dialog"
      >
        <DialogHeader>
          <div className="flex items-center justify-between gap-2 pr-8">
            <DialogTitle>{appointment?.id ? "Editar cita" : "Nueva cita"}</DialogTitle>
            {/* V1.35 · Toggle "Contexto" solo visible en pantallas < lg */}
            {studentId && (
              <button
                type="button"
                onClick={() => setShowContextMobile((v) => !v)}
                data-testid="appointment-context-toggle"
                className="lg:hidden inline-flex items-center gap-1 rounded-full bg-orange-50 border border-orange-300 text-orange-800 hover:bg-orange-100 text-xs font-semibold px-2.5 py-1"
              >
                {showContextMobile ? <XIcon className="h-3 w-3" /> : <Info className="h-3 w-3" />}
                {showContextMobile ? "Cerrar contexto" : "Contexto"}
              </button>
            )}
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4">
          <form onSubmit={submit} className="space-y-4 min-w-0">
          <TypeSelect
            value={form.appointment_type}
            onChange={(v) => setForm({ ...form, appointment_type: v })}
          />

          <StudentSelect
            value={form.student_id}
            onChange={(v) => setForm({ ...form, student_id: v })}
            students={activeStudents}
            metricsById={practiceMetrics}
          />

          {studentId && (
            <BonoIndicator
              summary={summary}
              summaryLoading={summaryLoading}
              appliesBono={appliesBono}
              onReload={loadSummary}
            />
          )}

          {studentId && form.appointment_type?.startsWith("examen") && (
            <ExamRequestPicker
              studentId={studentId}
              value={form.exam_request_id}
              onChange={(v, dgtId) => setForm({ ...form, exam_request_id: v, tasa_id: dgtId || "" })}
            />
          )}

          <div className="grid grid-cols-[1fr,1fr,110px,1fr] gap-3">
            <div>
              <label className="text-sm font-semibold text-neutral-700 mb-1.5 block">Fecha *</label>
              <input
                data-testid="appt-date-input"
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                required
                className="w-full rounded-xl border border-neutral-300 bg-white px-3 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-neutral-700 mb-1.5 block">Inicio *</label>
              <input
                data-testid="appt-time-input"
                type="time"
                value={form.time}
                onChange={(e) => handleTimeChange(e.target.value)}
                required
                className="w-full rounded-xl border border-neutral-300 bg-white px-3 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div className="flex flex-col gap-1">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-0.5 block">
                  Min/clase
                </label>
                <input
                  data-testid="appt-minutes-per-class-input"
                  type="number"
                  min="1"
                  step="1"
                  value={minutesPerClass}
                  onChange={(e) => handleMinutesPerClassChange(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-[22px] text-xs font-mono tabular-nums text-center focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-orange-600 mb-0.5 block">
                  Clases
                </label>
                <input
                  data-testid="appt-classes-count-input"
                  type="number"
                  min="1"
                  step="1"
                  value={classesCount}
                  onChange={(e) => handleClassesChange(e.target.value)}
                  className="w-full rounded-lg border border-orange-300 bg-orange-50 px-2 h-[22px] text-xs font-mono tabular-nums text-center font-bold text-orange-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-neutral-700 mb-1.5 block">Fin *</label>
              <input
                data-testid="appt-end-time-input"
                type="time"
                value={form.end_time}
                onChange={(e) => handleEndTimeChange(e.target.value)}
                required
                className="w-full rounded-xl border border-neutral-300 bg-white px-3 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          <SessionSummary time={form.time} endTime={form.end_time} consumesBono={appliesBono} />

          {/* V1.61 · Citas del día: visión rápida para evitar solapes */}
          {form.date && (
            <DayAgendaPanel
              date={form.date}
              time={form.time}
              endTime={form.end_time}
              excludeId={appointment?.id}
              students={students}
            />
          )}

          {/* V1.49 · Repetir semanalmente (solo al crear) */}
          {!appointment?.id && (
            <div className="rounded-xl border border-neutral-200 bg-neutral-50/60 overflow-hidden">
              <button
                type="button"
                onClick={() => {
                  setRepeatOpen((v) => {
                    const next = !v;
                    if (next && repeatDays.length === 0 && form.date) {
                      const d = new Date(form.date + "T00:00:00");
                      if (!isNaN(d.getTime())) setRepeatDays([d.getDay()]);
                    }
                    return next;
                  });
                }}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-neutral-600 hover:bg-neutral-100 transition-colors"
                data-testid="repeat-toggle"
              >
                <span className="inline-flex items-center gap-1.5 uppercase tracking-wider">
                  <Repeat className="h-3.5 w-3.5" />
                  Repetir semanalmente
                  {seriesActive && (
                    <span className="normal-case tracking-normal rounded-full bg-orange-100 text-orange-800 px-2 py-0.5 font-bold">
                      {seriesRows.length} citas
                    </span>
                  )}
                </span>
                {repeatOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {repeatOpen && (
                <div className="px-3 pb-3 pt-1 space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-1">
                      {[["L", 1], ["M", 2], ["X", 3], ["J", 4], ["V", 5], ["S", 6], ["D", 0]].map(([lbl, d]) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => toggleRepeatDay(d)}
                          data-testid={`repeat-day-${d}`}
                          className={
                            "h-8 w-8 rounded-full text-xs font-bold transition-colors " +
                            (repeatDays.includes(d)
                              ? "bg-orange-500 text-white"
                              : "bg-white border border-neutral-300 text-neutral-600 hover:bg-neutral-100")
                          }
                        >
                          {lbl}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-1.5 ml-auto">
                      <label className="text-[11px] font-semibold text-neutral-600">Semanas</label>
                      <input
                        type="number"
                        min="1"
                        max="26"
                        step="1"
                        value={repeatWeeks}
                        onChange={(e) => setRepeatWeeks(Math.min(26, Math.max(1, parseInt(e.target.value || 1, 10) || 1)))}
                        data-testid="repeat-weeks-input"
                        className="w-16 rounded-lg border border-neutral-300 bg-white px-2 h-8 text-xs font-mono tabular-nums text-center focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>
                  {repeatRows.length > 0 && (
                    <div className="rounded-lg border border-neutral-200 bg-white divide-y divide-neutral-100 max-h-56 overflow-y-auto">
                      {repeatRows.map((r) => (
                        <div
                          key={r.date}
                          data-testid={`repeat-row-${r.date}`}
                          className={"flex items-center gap-2 px-2.5 py-1.5 " + (r.enabled ? "" : "opacity-40")}
                        >
                          <input
                            type="checkbox"
                            checked={r.enabled}
                            onChange={() => toggleRow(r.date)}
                            data-testid={`repeat-row-check-${r.date}`}
                            className="h-4 w-4 accent-orange-500 shrink-0"
                          />
                          <span className="text-xs font-semibold text-neutral-800 capitalize w-36 shrink-0 truncate">
                            {formatDateES(r.date)}
                          </span>
                          <input
                            type="time"
                            value={r.time}
                            disabled={!r.enabled}
                            onChange={(e) => setRowTime(r.date, e.target.value)}
                            data-testid={`repeat-row-time-${r.date}`}
                            className="rounded-lg border border-neutral-300 bg-white px-1.5 h-7 text-xs font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                          <span className="text-[10px] text-neutral-400 font-mono tabular-nums shrink-0">–{r.end_time}</span>
                          {r.enabled && r.overlaps && r.overlaps.length > 0 && (
                            <span
                              data-testid={`repeat-row-conflict-${r.date}`}
                              className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-800 bg-amber-50 border border-amber-300 rounded-full px-2 py-0.5 min-w-0 truncate"
                              title={r.overlaps.map((o) => `${o.time}–${o.end_time} ${o.main_student?.name || ""}`).join(" · ")}
                            >
                              <AlertTriangle className="h-3 w-3 shrink-0" />
                              <span className="truncate">
                                Ya hay clase: {r.overlaps[0].time} {r.overlaps[0].main_student?.name?.split(" ")[0] || ""}
                                {r.overlaps.length > 1 ? ` +${r.overlaps.length - 1}` : ""}
                              </span>
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-neutral-500">
                    Los avisos ⚠️ indican que ya existe otra clase a esa hora: puedes crear igualmente, cambiar la hora de ese día o desmarcarlo.
                  </p>
                </div>
              )}
            </div>
          )}

          {availabilityWarnings.length > 0 && studentId && (
            <div
              data-testid="availability-warnings"
              className="rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-1.5"
            >
              {availabilityWarnings.map((w, idx) => (
                <div key={idx} className="flex items-start gap-2 text-xs" data-testid={`availability-warning-${w.code}`}>
                  <AlertTriangle className={"h-4 w-4 mt-0.5 shrink-0 " + (w.severity === "high" ? "text-red-600" : "text-amber-700")} />
                  <div className="flex-1">
                    <div className={"font-bold " + (w.severity === "high" ? "text-red-900" : "text-amber-900")}>
                      {w.severity === "high" ? "Incidencia activa" : "Aviso de disponibilidad"}
                    </div>
                    <div className={w.severity === "high" ? "text-red-800" : "text-amber-800"}>
                      {w.message}
                    </div>
                  </div>
                </div>
              ))}
              <div className="text-[10px] text-amber-800/70 pl-6">
                Consulta los <span className="font-semibold">huecos compatibles</span> en la barra de contexto (→) o programa igualmente — se marcará como "Fuera de disponibilidad declarada".
              </div>
            </div>
          )}

          {overlaps.length > 0 && studentId && (
            <div
              data-testid="overlap-notice"
              className="rounded-xl border border-blue-300 bg-blue-50 p-3 space-y-2"
            >
              <div className="flex items-start gap-2">
                <Users2 className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-blue-900 text-sm">
                    Ya existe otra clase a esta hora
                  </div>
                  <p className="text-xs text-blue-800/90 mt-0.5">
                    Puedes crear una cita paralela normal, o incorporar al alumno a la sesión existente.
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                {overlaps.map((o) => {
                  const cap = o.vehicle;
                  const capacityFull = cap && o.capacity_left === 0;
                  const formOpen = sharedForm && sharedForm.overlap.id === o.id;
                  return (
                    <div
                      key={o.id}
                      data-testid={`overlap-item-${o.id}`}
                      className="rounded-lg bg-white border border-blue-200 px-3 py-2.5 text-xs"
                    >
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="font-semibold text-neutral-900">
                          {o.time}–{o.end_time || "?"}
                        </span>
                        <span className="text-neutral-600">·</span>
                        <span className="text-neutral-800">{o.main_student?.name || "?"}</span>
                        {o.shared_students?.length > 0 && (
                          <span className="text-neutral-500">
                            +{o.shared_students.map((s) => s.name.split(" ")[0]).join(", ")}
                          </span>
                        )}
                        <span className="text-neutral-500">· {o.circuit}</span>
                        {cap && (
                          <span
                            className={
                              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold " +
                              (capacityFull
                                ? "bg-red-100 text-red-800"
                                : "bg-emerald-100 text-emerald-800")
                            }
                          >
                            <Car className="h-3 w-3" /> {cap.plate} · {o.total_students}/{cap.max_students}
                          </span>
                        )}
                      </div>

                      {!formOpen && (
                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={() => addAsShared(o, null)}
                            disabled={capacityFull}
                            data-testid={`overlap-add-shared-${o.id}`}
                            className={
                              "inline-flex items-center gap-1.5 rounded-lg px-3 h-8 text-xs font-semibold transition-colors " +
                              (capacityFull
                                ? "bg-neutral-100 text-neutral-400 cursor-not-allowed"
                                : "bg-blue-600 text-white hover:bg-blue-700")
                            }
                          >
                            <Users2 className="h-3.5 w-3.5" />
                            {capacityFull ? "Capacidad completa" : "Añadir como Alumno en sesión compartida"}
                          </button>
                        </div>
                      )}

                      {formOpen && (
                        <div
                          data-testid={`shared-form-${o.id}`}
                          className="mt-3 pt-3 border-t border-blue-200 space-y-2"
                        >
                          <div className="text-[11px] font-semibold uppercase tracking-wider text-blue-700">
                            Datos de recogida del nuevo participante
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-[10px] font-semibold text-neutral-600 mb-0.5 block">Hora de recogida *</label>
                              <input
                                type="time"
                                value={sharedForm.pickup_time}
                                onChange={(e) => setSharedForm({ ...sharedForm, pickup_time: e.target.value })}
                                data-testid={`shared-form-time-${o.id}`}
                                className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-9 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="text-[10px] font-semibold text-neutral-600 mb-0.5 block">Punto de recogida *</label>
                              <Select
                                key={`shared-loc-${locations.length}`}
                                value={sharedForm.pickup_location || "__none__"}
                                onValueChange={(v) => {
                                  if (v === QUICK_ADD_VALUE) {
                                    setQuickAdd({
                                      entity: "location",
                                      apply: async (doc) => {
                                        const { data } = await api.get("/locations");
                                        setLocations(data);
                                        setSharedForm((sf) => (sf ? { ...sf, pickup_location: doc.name } : sf));
                                      },
                                    });
                                    return;
                                  }
                                  setSharedForm({ ...sharedForm, pickup_location: v === "__none__" ? "" : v });
                                }}
                              >
                                <SelectTrigger data-testid={`shared-form-loc-${o.id}`} className="h-9 rounded-lg text-xs">
                                  <SelectValue placeholder="Selecciona" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__">— Selecciona —</SelectItem>
                                  {locations.map((l) => (
                                    <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>
                                  ))}
                                  <SelectItem value={QUICK_ADD_VALUE}>+ Añadir ubicación nueva…</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] font-semibold text-neutral-600 mb-0.5 block">Punto de finalización (opcional)</label>
                            <Select
                              key={`shared-endloc-${locations.length}`}
                              value={sharedForm.pickup_location_end || "__none__"}
                              onValueChange={(v) => {
                                if (v === QUICK_ADD_VALUE) {
                                  setQuickAdd({
                                    entity: "location",
                                    apply: async (doc) => {
                                      const { data } = await api.get("/locations");
                                      setLocations(data);
                                      setSharedForm((sf) => (sf ? { ...sf, pickup_location_end: doc.name } : sf));
                                    },
                                  });
                                  return;
                                }
                                setSharedForm({ ...sharedForm, pickup_location_end: v === "__none__" ? "" : v });
                              }}
                            >
                              <SelectTrigger data-testid={`shared-form-endloc-${o.id}`} className="h-9 rounded-lg text-xs">
                                <SelectValue placeholder="Misma que la de recogida" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">— Misma que la de recogida —</SelectItem>
                                {locations.map((l) => (
                                  <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>
                                ))}
                                <SelectItem value={QUICK_ADD_VALUE}>+ Añadir ubicación nueva…</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex justify-end gap-2 pt-1">
                            <button
                              type="button"
                              onClick={() => setSharedForm(null)}
                              className="btn-secondary !h-8 !text-[11px]"
                              data-testid={`shared-form-cancel-${o.id}`}
                            >
                              Cancelar
                            </button>
                            <button
                              type="button"
                              disabled={
                                !sharedForm.pickup_time ||
                                !sharedForm.pickup_location ||
                                addingShared === o.id
                              }
                              onClick={() => addAsShared(o, sharedForm)}
                              data-testid={`shared-form-confirm-${o.id}`}
                              className="inline-flex items-center gap-1.5 rounded-lg px-3 h-8 text-[11px] font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:bg-neutral-300 disabled:text-neutral-500"
                            >
                              <Users2 className="h-3.5 w-3.5" />
                              {addingShared === o.id ? "Añadiendo…" : "Confirmar y añadir"}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <LocationSelect
            label="Ubicación de inicio"
            testId="appt-location-select"
            value={form.pickup_location}
            onChange={(v) => setForm({ ...form, pickup_location: v })}
            locations={locations}
            onQuickAdd={() => setQuickAdd({
              entity: "location",
              apply: async (doc) => {
                const { data } = await api.get("/locations");
                setLocations(data);
                setForm((f) => ({ ...f, pickup_location: doc.name }));
              },
            })}
          />

          <LocationSelect
            label="Ubicación de fin (si es distinta)"
            testId="appt-location-end-select"
            value={form.pickup_location_end}
            onChange={(v) => setForm({ ...form, pickup_location_end: v })}
            locations={locations}
            optional
            onQuickAdd={() => setQuickAdd({
              entity: "location",
              apply: async (doc) => {
                const { data } = await api.get("/locations");
                setLocations(data);
                setForm((f) => ({ ...f, pickup_location_end: doc.name }));
              },
            })}
          />

          <div>
            <label className="text-sm font-semibold text-neutral-700 mb-1.5 block">Circuito *</label>
            <Select
              key={`circuit-${circuits.length}`}
              value={form.circuit}
              onValueChange={(v) => {
                if (v === QUICK_ADD_VALUE) {
                  setQuickAdd({
                    entity: "circuit",
                    apply: async (doc) => {
                      const { data } = await api.get("/circuits");
                      setCircuits(data);
                      setForm((f) => ({ ...f, circuit: doc.name }));
                    },
                  });
                  return;
                }
                setForm({ ...form, circuit: v });
              }}
            >
              <SelectTrigger data-testid="appt-circuit-select" className="h-11 rounded-xl">
                <SelectValue placeholder="Selecciona un circuito" />
              </SelectTrigger>
              <SelectContent>
                {circuits.map((c) => (
                  <SelectItem key={c.id} value={c.name}>
                    {c.name}
                  </SelectItem>
                ))}
                <SelectItem value={QUICK_ADD_VALUE} data-testid="appt-circuit-quick-add">
                  + Añadir ruta nueva…
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* V1.XX · Observaciones libres del profesor (editable siempre) */}
          <div>
            <label className="text-sm font-semibold text-neutral-700 mb-1.5 block">
              Observaciones
            </label>
            <textarea
              data-testid="appt-notes-input"
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              placeholder="Notas internas o instrucciones para el profesor…"
              className="w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
            />
          </div>

          <div className="rounded-xl border border-neutral-200 bg-neutral-50/60 overflow-hidden">
            <button
              type="button"
              onClick={() => setAdvancedOpen((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-neutral-600 hover:bg-neutral-100 transition-colors"
              data-testid="appt-advanced-toggle"
            >
              <span className="uppercase tracking-wider">Detalles avanzados</span>
              {advancedOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {advancedOpen && (
              <div className="px-3 pb-3 pt-1 space-y-3">
                <div>
                  <label className="text-xs font-semibold text-neutral-600 mb-1 block">Vehículo</label>
                  <Select
                    key={`vehicle-${vehicles.length}`}
                    value={form.vehicle_id || "__none__"}
                    onValueChange={(v) => {
                      if (v === QUICK_ADD_VALUE) {
                        setQuickAdd({
                          entity: "vehicle",
                          apply: async (doc) => {
                            const { data } = await api.get("/vehicles");
                            setVehicles(data);
                            setForm((f) => ({ ...f, vehicle_id: doc.id }));
                          },
                        });
                        return;
                      }
                      setForm({ ...form, vehicle_id: v === "__none__" ? "" : v });
                    }}
                  >
                    <SelectTrigger data-testid="appt-vehicle-select" className="h-10 rounded-lg">
                      <SelectValue placeholder="Sin vehículo asignado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Sin asignar —</SelectItem>
                      {vehicles.filter((v) => v.active).map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.plate} · {[v.brand, v.model].filter(Boolean).join(" ")} · máx {v.max_students}
                        </SelectItem>
                      ))}
                      <SelectItem value={QUICK_ADD_VALUE} data-testid="appt-vehicle-quick-add">
                        + Añadir vehículo nuevo…
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {form.vehicle_id && (
                    <p className="text-[10px] text-neutral-500 mt-1">
                      La capacidad del vehículo se aplica cuando se añaden alumnos en sesión compartida.
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-[11px] font-semibold text-neutral-600 mb-1 block">Profesor</label>
                    <select
                      value={form.teacher}
                      onChange={(e) => {
                        if (e.target.value === QUICK_ADD_VALUE) {
                          setQuickAdd({
                            entity: "teacher",
                            apply: async (doc) => {
                              const { data } = await api.get("/teachers", { params: { include_inactive: false } });
                              const seen = new Set();
                              const opts = (data || [])
                                .filter((p) => p.active !== false)
                                .map((p) => {
                                  const name = (p.name || "").trim();
                                  const parts = name.split(/\s+/).filter(Boolean);
                                  let code = "";
                                  if (parts.length >= 2) code = (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
                                  else if (parts.length === 1) code = parts[0].slice(0, 2).toUpperCase();
                                  return { code, label: name || code };
                                })
                                .filter((o) => o.code && !seen.has(o.code) && (seen.add(o.code), true));
                              setTeacherOptions(preferJoseAlfredo(opts));
                              const created = opts.find((o) => o.label === (doc?.name || "").trim());
                              if (created) setForm((f) => ({ ...f, teacher: created.code }));
                            },
                          });
                          return;
                        }
                        setForm({ ...form, teacher: e.target.value });
                      }}
                      data-testid="appt-teacher-input"
                      className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-9 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      {/* V1.38 · Fuente única `/teachers`. Si la sesión trae un
                          código antiguo (ej. "JA") que ya no aparece en la
                          lista, lo mantenemos visible para no perderlo. */}
                      {form.teacher && !teacherOptions.some((o) => o.code === form.teacher) && (
                        <option value={form.teacher}>{form.teacher} · (legacy)</option>
                      )}
                      {!form.teacher && <option value="">— Selecciona un profesor —</option>}
                      {teacherOptions.map((o) => (
                        <option key={o.code} value={o.code}>{o.code} · {o.label}</option>
                      ))}
                      <option value={QUICK_ADD_VALUE}>+ Añadir profesor nuevo…</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-neutral-600 mb-1 block">Franquicia</label>
                    <input
                      value={form.franchise}
                      onChange={(e) => setForm({ ...form, franchise: e.target.value })}
                      data-testid="appt-franchise-input"
                      className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-9 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-neutral-600 mb-1 block">Sección</label>
                    <input
                      value={form.section}
                      onChange={(e) => setForm({ ...form, section: e.target.value })}
                      data-testid="appt-section-input"
                      className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-9 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                </div>
                {/* V1.XX · Estado sólo editable cuando modificamos una cita
                    existente. En modo "Nueva cita" el estado inicial siempre
                    es `pending`. */}
                {appointment?.id && (
                  <div>
                    <label className="text-[11px] font-semibold text-neutral-600 mb-1 block">Estado</label>
                    <Select
                      value={form.status || "pending"}
                      onValueChange={(v) => setForm({ ...form, status: v })}
                    >
                      <SelectTrigger data-testid="appt-status-select" className="h-9 rounded-lg text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pendiente</SelectItem>
                        <SelectItem value="confirmed">Confirmada</SelectItem>
                        <SelectItem value="cancelled">Cancelada</SelectItem>
                        <SelectItem value="change_requested">Cambio solicitado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
          </div>

          {confirmingOver ? (
            <BonoOverConfirmation
              remaining={summary?.remaining ?? 0}
              onBack={() => setConfirmingOver(false)}
              onContinue={async () => {
                await doSave();
              }}
            />
          ) : (
            <DialogFooter className="gap-2 sm:gap-2">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                data-testid="appt-cancel-button"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-primary"
                data-testid="appt-save-button"
              >
                {saving ? "Guardando…" : seriesActive ? `Crear ${seriesRows.length} citas` : "Guardar"}
              </button>
            </DialogFooter>
          )}
        </form>

        {/* V1.35 · Sidebar de contexto en desktop; en móvil se muestra si el
            toggle "Contexto" está activo. */}
        <div className={"min-w-0 " + (showContextMobile ? "block" : "hidden lg:block")}>
          <AppointmentContextSidebar
            context={studentContext}
            loading={contextLoading}
            suggestions={suggestSlots}
            suggestLoading={suggestLoading}
            onApplySuggestion={applySuggestion}
            teacher={form.teacher}
            duration={classesCount * minutesPerClass}
            hasStudent={!!studentId}
          />
        </div>
      </div>
      <QuickAddDialog
        open={!!quickAdd}
        entity={quickAdd?.entity}
        onClose={() => setQuickAdd(null)}
        onCreated={async (doc) => {
          const apply = quickAdd?.apply;
          setQuickAdd(null);
          if (apply) await apply(doc);
        }}
      />
      </DialogContent>
    </Dialog>
  );
}
